import type { SeriesDatum } from "@/domains/analytics/components/charts/seriesShare";

export type PieMetric = "expense" | "income";

export type RankingSort = "amount" | "overspend" | "variance";

export type CategoryRankingMeta = {
  /** Amount spent above budget this cycle. Undefined when the category has no budget. */
  overage?: number;
  /** Projected deviation from the category's own baseline. */
  variance?: number;
};

const EXPENSE_SORTS: RankingSort[] = ["amount", "overspend", "variance"];

/** Income only ranks by amount; the budget and baseline criteria only describe spending. */
export const availableRankingSorts = (metric: PieMetric): RankingSort[] =>
  metric === "income" ? ["amount"] : EXPENSE_SORTS;

/**
 * The single category ranking for the page. Sorting replaces what used to be three
 * separate lists (by amount, by overspend, by projected variance) that ranked the same
 * categories differently with no explanation.
 */
export const sortRankingItems = (
  items: SeriesDatum[],
  meta: Map<number | string, CategoryRankingMeta>,
  sort: RankingSort,
): SeriesDatum[] => {
  if (sort === "amount") return [...items].sort((a, b) => b.value - a.value);

  const score = (id: number | string) => {
    const entry = meta.get(id);
    if (sort === "overspend") return entry?.overage ?? -1;
    return Math.abs(entry?.variance ?? 0);
  };

  return [...items].sort((a, b) => (score(b.id) ?? 0) - (score(a.id) ?? 0) || b.value - a.value);
};
